import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaCogs, FaWarehouse, FaUsers, FaChartBar, FaClock, FaBell, 
  FaChartLine, FaShieldAlt, FaDownload, FaCheckCircle, 
  FaStar, FaArrowRight, FaCode, FaGlobe, FaCalendarAlt, FaFileInvoiceDollar,
  FaTimes, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import './ERPPage.css';

const ERPPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Modal state management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const galleryImages = [
    '/erp/erp1.png',
    '/erp/erp2.png',
    '/erp/erp3.png',
    '/erp/erp4.png',
    '/erp/erp5.png'
  ];

  const openModal = (imageSrc) => {
    const index = galleryImages.indexOf(imageSrc);
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const features = [
    {
      icon: FaWarehouse,
      title: 'Skladové hospodárstvo',
      description: 'Komplexná správa skladov s automatickým sledovaním zásob, inventúrami a optimalizáciou'
    },
    {
      icon: FaFileInvoiceDollar,
      title: 'Objednávky a fakturácia',
      description: 'Automatizované spracovanie objednávok, generovanie faktúr a správa platieb'
    },
    {
      icon: FaClock,
      title: 'Dochádzka zamestnancov',
      description: 'Evidencia pracovného času, dochádzky a výpočet miezd s integráciou na personálny systém'
    },
    {
      icon: FaCalendarAlt,
      title: 'Plánovanie a scheduling',
      description: 'Pokročilé plánovanie zdrojov, kapacít a projektov s ganttovými diagramami'
    },
    {
      icon: FaBell,
      title: 'Notifikácie a workflow',
      description: 'Real-time notifikácie, automatizované workflow procesy a schvaľovacie mechanizmy'
    },
    {
      icon: FaChartBar,
      title: 'Reporting a analytika',
      description: 'Pokročilé reporty, dashboardy a business intelligence pre všetky moduly'
    }
  ];

  const benefits = [
    {
      icon: FaCogs,
      title: 'Automatizácia procesov',
      value: '80%',
      description: 'Zníženie manuálnej práce vďaka automatizácii'
    },
    {
      icon: FaChartLine,
      title: 'Efektivita firmy',
      value: '65%',
      description: 'Zvýšenie celkovej produktivity spoločnosti'
    },
    {
      icon: FaClock,
      title: 'Úspora času',
      value: '50%',
      description: 'Rýchlejšie spracovanie firemných procesov'
    },
    {
      icon: FaUsers,
      title: 'Spokojnosť tímov',
      value: '95%',
      description: 'Vyššia spokojnosť zamestnancov s modernými nástrojmi'
    }
  ];

  const packages = [
    {
      name: 'BASIC ERP',
      price: '899€',
      description: 'Pre malé a stredné firmy',
      features: [
        'Skladové hospodárstvo',
        'Základné objednávky',
        'Evidencia dochádzky',
        'Základné reporty',
        'Do 5 používateľov',
        'Email podpora'
      ]
    },
    {
      name: 'STANDARD ERP',
      price: '1599€',
      description: 'Pre rastúce spoločnosti',
      features: [
        'Všetky funkcie BASIC',
        'Pokročilé plánovanie',
        'Workflow a notifikácie',
        'Viacjazyčné rozhranie',
        'Do 25 používateľov',
        'SignalR real-time sync',
        'Prioritná podpora'
      ],
      popular: true
    },
    {
      name: 'PREMIUM ERP',
      price: '2999€',
      description: 'Pre veľké enterprise firmy',
      features: [
        'Všetky funkcie STANDARD',
        'Viacúrovňové schvaľovanie',
        'Custom moduly pre tímy',
        'API integrácie',
        'Neobmedzený počet používateľov',
        'Podpora 24/7',
        'Onsite implementácia a školenia'
      ]
    }
  ];

  const industries = [
    {
      icon: FaCogs,
      title: 'Výrobné spoločnosti',
      description: 'Riadenie výrobných procesov a kapacít'
    },
    {
      icon: FaWarehouse,
      title: 'Logistické firmy',
      description: 'Správa skladov a distribučných centier'
    },
    {
      icon: FaUsers,
      title: 'Služby a konzultácie',
      description: 'Projektové riadenie a evidencia času'
    },
    {
      icon: FaGlobe,
      title: 'Medzinárodné firmy',
      description: 'Viacjazyčné a viacúrovňové riešenia'
    }
  ];

  const testimonials = [
    {
      name: 'Ing. Peter Novák',
      company: 'MANUFACTURING PLUS s.r.o.',
      text: 'ERP systém nám spojil všetky procesy do jednej platformy. Prehľad nad firmou je teraz úplne iný.',
      rating: 5
    },
    {
      name: 'Mária Kováčová',
      company: 'GLOBAL TRADE',
      text: 'Viacjazyčné riešenie a notifikácie výrazne zlepšili komunikáciu medzi našimi pobočkami.',
      rating: 5
    }
  ];

  const technologies = [
    { name: 'React', color: '#61dafb' },
    { name: 'C#', color: '#239120' },
    { name: 'MSSQL', color: '#cc2927' },
    { name: 'SignalR', color: '#512bd4' },
    { name: 'MUI', color: '#007fff' }
  ];

  return (
    <div className="erp-page">
      {/* Hero Section */}
      <section className="erp-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>ERP</h1>
              <h2>Komplexný ERP systém</h2>
              <p>
                Komplexný ERP systém, ktorý spája všetky firemné procesy do jednej platformy – 
                od skladov a objednávok až po dochádzku, plánovanie a reporting. 
                Viacjazyčné, viacúrovňové riešenie s notifikáciami a modulmi pre rôzne tímy.
              </p>
              <div className="hero-actions">
                <Link to="/kontakt" className="btn btn-primary">
                  <FaArrowRight />Žiadosť o ponuku
                </Link>
              </div>
            </div>
            <div className="hero-image">
              <img src="/erp.png" alt="ERP System" className="hero-product-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <h2>Ukážky systému</h2>
          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <div key={index} className="gallery-item" onClick={() => openModal(image)}>
                <img src={image} alt={`ERP System - ukážka ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section">
        <div className="container">
          <h2>Použité technológie</h2>
          <div className="technologies-grid">
            {technologies.map((tech, index) => (
              <div key={index} className="technology-card">
                <div className="tech-name" style={{ color: tech.color }}>
                  {tech.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2>Pokročilé funkcie systému</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  <feature.icon />
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2>Výsledky, ktoré môžete očakávať</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">
                  <benefit.icon />
                </div>
                <div className="benefit-value">{benefit.value}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="industries-section">
        <div className="container">
          <h2>Vhodné pre všetky typy podnikov</h2>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-icon">
                  <industry.icon />
                </div>
                <h4>{industry.title}</h4>
                <p>{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Section */}
      <section className="customers-section">
        <div className="container">
          <h2>Náš klient</h2>
          <div className="customers-grid">
            <a 
              href="https://www.crocodille.com/cs/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="customer-card"
            >
              <img src="/crocodille_logo.png" alt="Crocodille ČR" />
              <h3>Crocodille ČR, spol. s r. o.</h3>
              <p>Firma Crocodille ČR sa zaoberá výrobou a predajom v segmente FMCG (obložené bagety a sendviče). Je dlhodobo jednotkou trhu v Českej republike.</p>
            </a>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {isModalOpen && (
        <div className="image-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <FaTimes />
            </button>
            <button className="modal-prev" onClick={prevImage}>
              <FaChevronLeft />
            </button>
            <img src={galleryImages[currentImageIndex]} alt={`ERP System - ukážka ${currentImageIndex + 1}`} />
            <button className="modal-next" onClick={nextImage}>
              <FaChevronRight />
            </button>
            <div className="modal-counter">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPPage;